import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Icon } from '../../../../components/common/icon/Icon';
import { FavoriteButton } from './FavoriteButton';
import { CropPriceSummaryCard, LastYearWeeklyTrend } from './CropPriceSummaryCard';
import { IGoToCropDetailPageParam } from '../CropTrendsList';
import { useDeleteCropFavorite } from '../../../../hooks/api/crop-trends/mutations/useDeleteCropFavorite.mutation';
import { PopupConfirmModal } from '../../../../components/common/modal/popup/PopupConfirmModal';
import { useGetCropFavoritePrices } from '../../../../hooks/api/crop-trends/useGetCropFavoritePrices.query';
import { ampli } from '../../../../ampli';
import { useCheckUserLoggedIn } from '../../../../hooks/user/useCheckUserLoggedIn';

interface FavoriteCropSectionProps {
  goToCropDetailPage: ({
    marketCode,
    largeCategoryCode,
    middleCategoryCode,
    smallCategoryCode,
  }: IGoToCropDetailPageParam) => void;
}

interface ICropCodeInfo {
  largeCategoryCode: string;
  middleCategoryCode: string;
  smallCategoryCode: string;
  smallCategoryName: string;
}

export const FavoriteCropSection = ({ goToCropDetailPage }: FavoriteCropSectionProps) => {
  const [isDeleteConfirmModalShow, setIsDeleteConfirmModalShow] = useState<boolean>(false);

  const { isUserLoggedIn } = useCheckUserLoggedIn();
  const { data: favoriteCropPrices } = useGetCropFavoritePrices();

  const { mutate: deleteCropFavorite } = useDeleteCropFavorite();

  const [selectedCropCodeInfo, setSelectedCropCodeInfo] = useState<ICropCodeInfo>();

  const onFavoriteClick = ({
    largeCategoryCode,
    middleCategoryCode,
    smallCategoryCode,
    smallCategoryName,
  }: ICropCodeInfo) => {
    setIsDeleteConfirmModalShow(true);

    setSelectedCropCodeInfo({ largeCategoryCode, middleCategoryCode, smallCategoryCode, smallCategoryName });
  };

  if (isUserLoggedIn && favoriteCropPrices && favoriteCropPrices.length > 0) {
    return (
      <>
        <Container>
          <SectionTitle>{'관심 농산물 시세'}</SectionTitle>
          {favoriteCropPrices?.map((favoriteCropPrice) => {
            return (
              <FavoriteCropContainer key={favoriteCropPrice.smallCategoryCode}>
                <AbsoluteWrapper>
                  <FavoriteButton
                    isFavorite
                    onFavoriteClick={() =>
                      onFavoriteClick({
                        largeCategoryCode: favoriteCropPrice.largeCategoryCode,
                        middleCategoryCode: favoriteCropPrice.middleCategoryCode,
                        smallCategoryCode: favoriteCropPrice.smallCategoryCode,
                        smallCategoryName: favoriteCropPrice.smallCategoryName,
                      })
                    }
                  />
                </AbsoluteWrapper>
                <ContentsContainer>
                  <HeaderContainer>
                    <Title>{favoriteCropPrice.smallCategoryName}</Title>
                  </HeaderContainer>

                  {favoriteCropPrice.wholesaleMarket.map((wholesaleMarket, index) => {
                    return (
                      <CropCardWrapper
                        key={favoriteCropPrice.smallCategoryCode + wholesaleMarket.wholesaleMarketCode}
                        onClick={() => {
                          ampli.pvmClickCropCardInFavoriteCrop({
                            cultivar: favoriteCropPrice.smallCategoryName,
                            price_changed_history: wholesaleMarket.lastYearWeeklyTrend,
                          });

                          goToCropDetailPage({
                            marketCode: wholesaleMarket.wholesaleMarketCode,
                            largeCategoryCode: favoriteCropPrice.largeCategoryCode,
                            middleCategoryCode: favoriteCropPrice.middleCategoryCode,
                            middleCategoryName: favoriteCropPrice.middleCategoryCode,
                            smallCategoryCode: favoriteCropPrice.smallCategoryCode,
                          });
                        }}
                        padding={index === 0 ? '16px 0px 8px 0px' : '8px 0px 8px 0px'}
                        isLastItem={favoriteCropPrice.wholesaleMarket.length - 1 === index}
                      >
                        <CropPriceSummaryCard
                          label={wholesaleMarket.wholesaleMarketName}
                          todayAveragePrice={wholesaleMarket.todayAveragePrice}
                          previousPrice={wholesaleMarket.dailyChange.previousPrice}
                          changeAmount={wholesaleMarket.dailyChange.changeAmount}
                          changeRate={wholesaleMarket.dailyChange.changeRate}
                          lastYearWeeklyTrend={wholesaleMarket.lastYearWeeklyTrend}
                          labelVariant={'normal'}
                        />
                      </CropCardWrapper>
                    );
                  })}
                </ContentsContainer>
              </FavoriteCropContainer>
            );
          })}
        </Container>
        <PopupConfirmModal
          isShow={isDeleteConfirmModalShow}
          closeModal={() => setIsDeleteConfirmModalShow(false)}
          title={'🗑 관심 농산물 삭제'}
          description={'선택한 항목을\n관심 농산물에서 삭제할게요!'}
          buttonArray={[
            {
              text: '확인',
              onClick: () => {
                deleteCropFavorite(
                  {
                    searchParam: {
                      largeCategoryCode: selectedCropCodeInfo?.largeCategoryCode ?? '',
                      middleCategoryCode: selectedCropCodeInfo?.middleCategoryCode ?? '',
                      smallCategoryCode: selectedCropCodeInfo?.smallCategoryCode ?? '',
                    },
                  },
                  {
                    onSuccess: () => {
                      ampli.pvmDeleteFavoriteCrop({ cultivar: selectedCropCodeInfo?.smallCategoryName ?? '' });
                      setIsDeleteConfirmModalShow(false);
                    },
                  },
                );
              },
              color: 'blue',
            },
            {
              text: '닫기',
              onClick: () => setIsDeleteConfirmModalShow(false),
              color: 'gray',
            },
          ]}
        />
      </>
    );
  }

  return null;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;

  padding: 16px 16px 0px 16px;
`;

const SectionTitle = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 23.4px */
`;

const FavoriteCropContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;

  position: relative;

  padding: 16px;

  border-radius: var(--radius-large, 16px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const HeaderContainer = styled.div`
  width: 100%;

  display: flex;
  align-items: flex-start;
  gap: 2px;
  align-self: stretch;

  padding-bottom: 16px;

  border-bottom: 1px solid var(--Light-Scheme-Monochrome-grey30, #e0e0eb);
`;

const Title = styled.span`
  flex: 1 0 0;

  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Medium/m-bold */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 18.2px */
`;

const ContentsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 0 0;

  position: relative;
`;

const CropCardWrapper = styled.div<{ padding: string; isLastItem?: boolean }>`
  width: 100%;

  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 4px;
  flex: 1 0 0;

  padding: ${({ padding }) => padding};

  border-bottom: ${({ isLastItem }) =>
    isLastItem ? 'none' : '1px solid var(--Light-Scheme-Monochrome-grey30, #e0e0eb)'};

  cursor: pointer;
`;

const AbsoluteWrapper = styled.span`
  position: absolute;
  top: 9px;
  right: 9px;

  z-index: 10;
`;
